/**
 * useCoverage Hook
 * 
 * Handles coverage/data sources fetching for admin view.
 * Encapsulates API calls per Rule 6.
 */

import { useState, useEffect, useCallback } from 'react';
import { authApi } from '../shared/utils/api';
import { GeoDataSource } from '../shared/types/parcel';
import { logger } from '../shared/utils/logger';

// Re-export for convenience
export type { GeoDataSource };

interface UseCoverageReturn {
  dataSources: GeoDataSource[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  stats: {
    totalSources: number;
    activeSources: number;
    statesCovered: number;
    countiesCovered: number;
  };
}

export function useCoverage(): UseCoverageReturn {
  const [dataSources, setDataSources] = useState<GeoDataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDataSources = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authApi.getParcelDataSources();
      if (response.success && response.sources) {
        setDataSources(response.sources);
      } else {
        setError('Failed to load data sources');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load coverage data';
      logger.error('Coverage fetch failed', err instanceof Error ? err : undefined, 'coverage');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDataSources();
  }, [fetchDataSources]);

  // Compute stats
  const activeSources = dataSources.filter(s => s.is_active);
  const states = new Set(dataSources.map(s => s.state_code).filter(Boolean));
  const counties = new Set(dataSources.map(s => s.county_name).filter(Boolean));

  return {
    dataSources,
    loading,
    error,
    refresh: fetchDataSources,
    stats: {
      totalSources: dataSources.length,
      activeSources: activeSources.length,
      statesCovered: states.size,
      countiesCovered: counties.size,
    },
  };
}

